import { createSignal } from 'solid-js'

/**
 * Creates the state shared by the entry edit modals: an open flag, the index of
 * the entry being edited, one string input per field, and a validation error
 * that clears whenever an input changes.
 *
 * @param fields - The input field keys.
 * @returns The modal state accessors and mutators.
 */
export function createEditModalState<Field extends string>(fields: readonly Field[]) {
   /**
    * Builds an all-empty input record.
    *
    * @returns Empty strings keyed by field.
    */
   const emptyValues = () =>
      Object.fromEntries(fields.map(field => [field, ''])) as Record<Field, string>

   const [isOpen, setOpen] = createSignal(false)
   const [editingIndex, setEditingIndex] = createSignal<number | null>(null)
   const [values, setValues] = createSignal<Record<Field, string>>(emptyValues())
   const [error, setError] = createSignal('')

   /**
    * Opens the modal for an entry with pre-filled inputs.
    *
    * @param index - The entry index being edited.
    * @param initialValues - The initial input values.
    */
   const open = (index: number, initialValues: Record<Field, string>) => {
      setEditingIndex(index)
      setValues(() => initialValues)
      setError('')
      setOpen(true)
   }

   /**
    * Closes the modal and clears its inputs and error.
    */
   const close = () => {
      setOpen(false)
      setEditingIndex(null)
      setValues(() => emptyValues())
      setError('')
   }

   /**
    * Sets one input value, clearing any error.
    *
    * @param field - The field key.
    * @param value - The raw input value.
    */
   const setValue = (field: Field, value: string) => {
      setValues(previous => ({ ...previous, [field]: value }))
      setError('')
   }

   /**
    * Returns an accessor for one input value.
    *
    * @param field - The field key.
    * @returns The accessor.
    */
   const valueAccessor = (field: Field) => () => values()[field]

   return { isOpen, editingIndex, error, setError, open, close, setValue, valueAccessor }
}
